import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Secondary capture-toolbar tools (S3 / U1).
 *
 * The capture toolbar used to be a single flat strip of up to ten 28×28 px icon-only buttons inside
 * `overflow-x-auto scrollbar-hide` — so on a phone, which is the primary capture device, targets
 * were half the 44 px minimum and any control past the fold scrolled out of view with no
 * affordance that it existed. Meaning was carried entirely by tooltips, which do not exist on touch.
 *
 * Bumping every button to 44 px in one row is not possible on a 360 px viewport (7 secondary
 * actions can be live at once), so the fix has two halves: primary actions stay inline at 44 px,
 * and the secondary ones collapse into this labelled overflow on narrow viewports. Collapsed, every
 * action finally carries a visible text label rather than a hover-only tooltip.
 *
 * Descriptor-driven on purpose: it gives S8 a `CaptureToolbar` seam to extract into without having
 * to untangle conditional JSX first.
 */

/** 44px targets. On a short phone the row yields height to the picture. */
export const CAPTURE_TOOLBAR_TARGET_CLASS =
  'h-11 w-11 [@media(max-width:767px)_and_(max-height:740px)]:h-8 [@media(max-width:767px)_and_(max-height:740px)]:w-8';
export const CAPTURE_TOOLBAR_ICON_CLASS = 'h-4 w-4';

export type CaptureToolbarActionTone = 'default' | 'destructive';

export interface CaptureToolbarAction {
  /** Stable key — also the test hook (`data-testid={`capture-tool-${id}`}`). */
  id: string;
  /** Visible text when collapsed; tooltip and `aria-label` when inline. Must stand alone. */
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /**
   * Toggled-on. Rendered with a ring as well as a colour shift so the state is not conveyed by
   * colour alone (WCAG 1.4.1).
   */
  active?: boolean;
  tone?: CaptureToolbarActionTone;
}

export interface CaptureToolbarSecondaryProps {
  actions: CaptureToolbarAction[];
  /** Collapse into the overflow popover — true on narrow viewports. */
  collapsed: boolean;
  /** Shell-matched button styling supplied by the host toolbar. */
  buttonClass: string;
}

const toneClass = (tone: CaptureToolbarActionTone | undefined, active: boolean | undefined): string => {
  if (tone === 'destructive') return 'text-destructive hover:text-destructive hover:bg-destructive/20';
  if (active) return 'text-primary bg-primary/20 ring-2 ring-primary';
  return '';
};

export const CaptureToolbarSecondary: React.FC<CaptureToolbarSecondaryProps> = ({
  actions,
  collapsed,
  buttonClass,
}) => {
  const visible = actions.filter(Boolean);
  if (visible.length === 0) return null;

  if (!collapsed) {
    return (
      <>
        {visible.map((action) => (
          <Tooltip key={action.id}>
            <TooltipTrigger asChild>
              <Button
                onClick={action.onClick}
                disabled={action.disabled}
                variant="ghost"
                size="icon"
                data-testid={`capture-tool-${action.id}`}
                aria-label={action.label}
                aria-pressed={action.active === undefined ? undefined : action.active}
                className={cn(
                  CAPTURE_TOOLBAR_TARGET_CLASS,
                  'rounded-full',
                  buttonClass,
                  toneClass(action.tone, action.active)
                )}
              >
                {action.icon}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{action.label}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="capture-tools-overflow"
          aria-label={`More capture tools (${visible.length})`}
          className={cn(CAPTURE_TOOLBAR_TARGET_CLASS, 'rounded-full', buttonClass)}
        >
          <MoreHorizontal className={CAPTURE_TOOLBAR_ICON_CLASS} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-60 p-1">
        <div className="flex flex-col" role="group" aria-label="Capture tools">
          {visible.map((action) => (
            <Button
              key={action.id}
              onClick={action.onClick}
              disabled={action.disabled}
              variant="ghost"
              data-testid={`capture-tool-${action.id}`}
              aria-pressed={action.active === undefined ? undefined : action.active}
              className={cn(
                'h-11 w-full justify-start gap-3 px-3 font-normal',
                action.tone === 'destructive' && 'text-destructive hover:text-destructive',
                action.active && 'bg-accent font-medium'
              )}
            >
              <span className="shrink-0" aria-hidden="true">
                {action.icon}
              </span>
              <span className="truncate">{action.label}</span>
              {action.active && (
                <span className="ml-auto text-xs text-muted-foreground shrink-0">On</span>
              )}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CaptureToolbarSecondary;
